import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { AdminFromCompanyDetailState } from '../../Breadcrumbs';
import { useAdminBreadcrumbBack } from '../../../hooks/useAdminBreadcrumbBack';
import {
    queryKeys,
    useAdminCompany,
    useAdminCompanyAdminMutations,
    useAdminCompanyAdmins,
    useAdminCompanyMutations,
    useAdminCompanyPulseSubscriptionMutations,
    useAdminCompanyPulseSubscriptions,
    useAdminCompanyTeamActiveMutation,
    useAdminCompanyTeamMembershipMutation,
    useAdminCompanyTeams,
    useAdminCreditPulseSubscriptionTiers,
    useAdminUserMutations,
    useAdminUsers,
    usePricingConfigs,
    useSows,
} from '../../../hooks/useApi';
import { ADMIN_SEARCH_DEBOUNCE_MS } from '../adminDashboardHelpers';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import {
    useAccessApi,
    type AdminCompanySyncResponse,
    type AdminCompanyAdminStatus,
    type AdminPulseSubscription,
    type AdminPulseSubscriptionInput,
    type AdminUserSyncResponse,
    type AdminUserRow,
} from '../../../services/api';
import { toCompanySowCreditRows } from './adminCreditAdapters';
import { hasCompanyCustomizationAccess } from './companyDetailAccess';
import { getCompanyDetailDisplayName } from './companyDetailName';
import {
    buildCompanyDetailSearchParams,
    getDefaultCompanyDetailTab,
    parseCompanyDetailTabParam,
    type CompanyDetailTab,
} from './companyDetailTabs';
import { getCompanySsoDetailState } from './companySsoDetail';
import { clearAdminToastTimer, emptyAdminToast, type AdminToastState } from '../adminToastTypes';

const COMPANY_USERS_PAGE_SIZE = 25;

function getNavigatedCompanyLabel(
    navState: AdminFromCompanyDetailState | null | undefined,
    companyId: string | undefined,
): string | undefined {
    const detail = navState?.adminCompanyDetail;
    return detail && detail.companyId === companyId ? detail.companyLabel : undefined;
}

export function useCompanyDetail() {
    const { companyId } = useParams<{ companyId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { goBack: goBackToAdmin } = useAdminBreadcrumbBack();
    const [searchParams, setSearchParams] = useSearchParams();
    const numericCompanyId = Number(companyId);
    const [activeTab, setActiveTab] = useState<CompanyDetailTab>(() =>
        getDefaultCompanyDetailTab(),
    );
    const [isCreateSowOpen, setIsCreateSowOpen] = useState(false);
    const [isHistoricalImportOpen, setIsHistoricalImportOpen] = useState(false);
    const [isCreateTeamOpen, setIsCreateTeamOpen] = useState(false);
    const [isEditCompanyOpen, setIsEditCompanyOpen] = useState(false);
    const [isPulseSubscriptionDialogOpen, setIsPulseSubscriptionDialogOpen] = useState(false);
    const [selectedPulseSubscription, setSelectedPulseSubscription] =
        useState<AdminPulseSubscription | null>(null);
    const [inlineTeamEdit, setInlineTeamEdit] = useState<{
        teamId: number;
        draft: string;
        originalName: string;
        error?: string;
    } | null>(null);
    const inlineTeamNameInputRef = useRef<HTMLInputElement>(null);
    const [selectedUser, setSelectedUser] = useState<AdminUserRow | null>(null);
    const [syncingUserId, setSyncingUserId] = useState<number | null>(null);
    const [companyAdminPendingUserId, setCompanyAdminPendingUserId] = useState<number | null>(null);
    const [isCompanyAuth0Syncing, setIsCompanyAuth0Syncing] = useState(false);
    const [teamMembershipPending, setTeamMembershipPending] = useState<{
        teamId: number;
        userId: number;
    } | null>(null);
    const [toast, setToast] = useState<AdminToastState>(emptyAdminToast);
    const toastTimerRef = useRef<number | null>(null);
    const [expandedTeamIds, setExpandedTeamIds] = useState<Set<string>>(() => new Set());
    const [usersPage, setUsersPage] = useState(1);
    const [usersSearchDraft, setUsersSearchDraft] = useState('');
    const [usersSearchQuery, setUsersSearchQuery] = useState('');
    const teamMembershipUpdateLockedRef = useRef(false);
    const queryClient = useQueryClient();
    const { getOrganizations, getSSOConnectionStatus } = useAccessApi();
    const { syncUserToAuth0SSE, syncUsersToAuth0, invalidateUserData } = useAdminUserMutations();
    const { syncCompanyToAuth0SSE } = useAdminCompanyMutations();
    const { hasPermission } = useUserPermissions();
    const canSyncCompanyUsers = hasPermission('administrate:users');

    useEffect(() => {
        setUsersPage(1);
        setExpandedTeamIds(new Set());
        setUsersSearchDraft('');
        setUsersSearchQuery('');
    }, [companyId]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setUsersSearchQuery(usersSearchDraft);
            setUsersPage(1);
        }, ADMIN_SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [usersSearchDraft]);

    useEffect(() => {
        const tab = searchParams.get('tab');
        const parsedTab = parseCompanyDetailTabParam(tab);
        if (parsedTab) {
            setActiveTab(parsedTab);
        }
        const expandTeam = searchParams.get('expandTeam');
        if (expandTeam) {
            setExpandedTeamIds(new Set([expandTeam]));
        }
    }, [companyId, searchParams]);

    const selectActiveTab = (tab: CompanyDetailTab) => {
        setActiveTab(tab);
        setSearchParams(buildCompanyDetailSearchParams(tab), { replace: true });
    };

    const inlineTeamFocusKey = inlineTeamEdit?.teamId ?? null;
    useEffect(() => {
        if (inlineTeamFocusKey === null) {
            return;
        }
        inlineTeamNameInputRef.current?.focus();
    }, [inlineTeamFocusKey]);

    const organizationsQuery = useQuery({
        queryKey: [...queryKeys.all, 'organizations'],
        queryFn: getOrganizations,
        staleTime: 5 * 60 * 1000,
    });
    const organization = (organizationsQuery.data ?? []).find(
        (candidate) => Number(candidate.metadata?.company_id) === numericCompanyId,
    );

    const companyQuery = useAdminCompany(
        Number.isFinite(numericCompanyId) && numericCompanyId > 0 ? numericCompanyId : null,
    );
    const companyAdminsQuery = useAdminCompanyAdmins(
        Number.isFinite(numericCompanyId) && numericCompanyId > 0 ? numericCompanyId : null,
    );
    const companyAdminMutations = useAdminCompanyAdminMutations(
        Number.isFinite(numericCompanyId) && numericCompanyId > 0 ? numericCompanyId : null,
    );
    const sowsQuery = useSows({ companyId: numericCompanyId || undefined }, true);
    const pricingQuery = usePricingConfigs(false, true);
    const pulseSubscriptionTiersQuery = useAdminCreditPulseSubscriptionTiers({
        forSowCreation: true,
        enabled: true,
    });
    const pulseSubscriptionsQuery = useAdminCompanyPulseSubscriptions(
        Number.isFinite(numericCompanyId) && numericCompanyId > 0 ? numericCompanyId : null,
    );
    const pulseSubscriptionMutations = useAdminCompanyPulseSubscriptionMutations(numericCompanyId);
    const teamsQuery = useAdminCompanyTeams(
        Number.isFinite(numericCompanyId) && numericCompanyId > 0 ? numericCompanyId : null,
    );
    const setTeamActive = useAdminCompanyTeamActiveMutation(
        Number.isFinite(numericCompanyId) && numericCompanyId > 0 ? numericCompanyId : null,
    );
    const setTeamMembershipActive = useAdminCompanyTeamMembershipMutation(
        Number.isFinite(numericCompanyId) && numericCompanyId > 0 ? numericCompanyId : null,
    );
    const companyUsersListQuery = usersSearchQuery.trim() || undefined;
    const companyUsersQuery = useAdminUsers(
        {
            companyId: numericCompanyId,
            page: usersPage,
            pageSize: COMPANY_USERS_PAGE_SIZE,
            query: companyUsersListQuery,
        },
        Number.isFinite(numericCompanyId) && numericCompanyId > 0,
    );
    const navState = location.state as AdminFromCompanyDetailState | null | undefined;
    const navigatedCompanyLabel = getNavigatedCompanyLabel(navState, companyId);
    const companyDisplayName = getCompanyDetailDisplayName({
        companyId: companyId ?? '',
        navigatedCompanyLabel,
        organizationDisplayName: organization?.display_name,
        organizationName: organization?.name,
        adminCompanyName: companyQuery.data?.name,
    });

    const organizationId = companyQuery.data?.organizationId?.trim() || organization?.id || null;
    const auth0OrganizationId = companyQuery.data?.organizationId?.trim() ?? '';
    const isCompanySyncedToAuth0 = auth0OrganizationId.length > 0;
    const ssoConnectionStatusQuery = useQuery({
        queryKey: [...queryKeys.all, 'sso-connection-status', organizationId],
        queryFn: async () => {
            if (!organizationId) {
                throw new Error('Organization ID is required');
            }
            const response = await getSSOConnectionStatus(organizationId);
            return response.success ? response.data : null;
        },
        enabled: Boolean(organizationId),
        staleTime: 2 * 60 * 1000,
    });
    const ssoDetailState = getCompanySsoDetailState({
        companyId: companyId ?? '',
        auth0OrganizationId,
        connectionStatus: ssoConnectionStatusQuery.data ?? null,
        hasConnectionStatusError: ssoConnectionStatusQuery.isError,
        isLoadingConnectionStatus: ssoConnectionStatusQuery.isLoading,
    });
    const companyCustomizations = companyQuery.data?.customizations ?? {};
    const companyHasPulseAccess = hasCompanyCustomizationAccess(
        companyCustomizations,
        'automatedInsights',
    );
    const companyHasProAccess = hasCompanyCustomizationAccess(companyCustomizations, 'core');

    const sowRows = useMemo(
        () => toCompanySowCreditRows(sowsQuery.data?.sows ?? []),
        [sowsQuery.data?.sows],
    );
    const companyAdminStatuses = useMemo(
        () => companyAdminsQuery.data?.companyAdmins ?? [],
        [companyAdminsQuery.data?.companyAdmins],
    );
    const companyAdminStatusByUserId = useMemo(() => {
        const statusByUserId = new Map<number, AdminCompanyAdminStatus>();
        for (const status of companyAdminStatuses) {
            statusByUserId.set(status.userId, status);
        }
        return statusByUserId;
    }, [companyAdminStatuses]);
    const companyAdminUserIds = useMemo(() => {
        return new Set(
            companyAdminStatuses.filter((status) => status.isAdmin).map((status) => status.userId),
        );
    }, [companyAdminStatuses]);

    const clearToastTimer = useCallback(() => clearAdminToastTimer(toastTimerRef), []);

    useEffect(() => clearToastTimer, [clearToastTimer]);

    const showSuccessToast = (message: string) => {
        clearToastTimer();
        setToast({ show: true, message, type: 'success' });
        toastTimerRef.current = window.setTimeout(() => setToast(emptyAdminToast), 3000);
    };

    const showWarningToast = (message: string) => {
        clearToastTimer();
        setToast({ show: true, message, type: 'warning' });
    };

    const showErrorToast = (message: string) => {
        clearToastTimer();
        setToast({ show: true, message, type: 'error' });
    };

    const dismissToast = () => {
        clearToastTimer();
        setToast(emptyAdminToast);
    };

    const finishInlineTeamNameEdit = async () => {
        if (!inlineTeamEdit) {
            return;
        }
        const { teamId, draft, originalName } = inlineTeamEdit;
        const trimmed = draft.trim();
        if (!trimmed) {
            setInlineTeamEdit({ ...inlineTeamEdit, error: 'Team name is required.' });
            return;
        }
        if (trimmed === originalName.trim()) {
            setInlineTeamEdit(null);
            return;
        }
        try {
            await setTeamActive.mutateAsync({ teamId, name: trimmed });
            showSuccessToast('Team updated.');
            setInlineTeamEdit(null);
        } catch {
            showErrorToast('Could not update team. Try again.');
        }
    };

    const handleSyncUser = async (userId: number) => {
        setSyncingUserId(userId);
        try {
            const result = await new Promise<AdminUserSyncResponse>((resolve, reject) => {
                void syncUserToAuth0SSE(
                    userId,
                    () => undefined,
                    reject,
                    (completedResult) => {
                        if (completedResult) {
                            resolve(completedResult);
                            return;
                        }
                        reject(new Error('User Auth0 sync finished without a result'));
                    },
                );
            });
            if (result.success) {
                showSuccessToast('User synced to Auth0.');
                await invalidateUserData();
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.adminCompanyTeams(numericCompanyId),
                });
                return;
            }

            showErrorToast(result.reason ?? 'Could not sync user to Auth0. Try again.');
        } catch (err) {
            showErrorToast(
                err instanceof Error && err.message
                    ? err.message
                    : 'Could not sync user to Auth0. Try again.',
            );
        } finally {
            setSyncingUserId(null);
        }
    };

    const handleSyncCompanyToAuth0 = async (syncUsers = false) => {
        if (!companyId) {
            return;
        }
        setIsCompanyAuth0Syncing(true);
        try {
            const result = await new Promise<AdminCompanySyncResponse>((resolve, reject) => {
                void syncCompanyToAuth0SSE(
                    {
                        companyId: numericCompanyId,
                        syncUsers,
                        unsyncedOnly: true,
                    },
                    () => undefined,
                    reject,
                    (completedResult) => {
                        if (completedResult) {
                            resolve(completedResult);
                            return;
                        }
                        reject(new Error('Company Auth0 sync finished without a result'));
                    },
                );
            });
            if (syncUsers && result.users) {
                showSuccessToast(
                    `Company synced to Auth0. User sync: ${result.users.synced} synced, ${result.users.failed} failed, ${result.users.skipped} skipped.`,
                );
            } else {
                showSuccessToast('Company organization synced to Auth0.');
            }
            void queryClient.invalidateQueries({
                queryKey: queryKeys.adminCompanyTeams(numericCompanyId),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.adminCompany(numericCompanyId),
            });
            void queryClient.invalidateQueries({ queryKey: [...queryKeys.all, 'organizations'] });
        } catch {
            showErrorToast('Could not sync company to Auth0. Try again.');
        } finally {
            setIsCompanyAuth0Syncing(false);
        }
    };

    const handleSyncAllUnsyncedUsers = async () => {
        if (!companyId) {
            return;
        }
        try {
            const result = await syncUsersToAuth0.mutateAsync({
                companyId: numericCompanyId,
                unsyncedOnly: true,
            });
            showSuccessToast(
                `Auth0 user sync finished: ${result.synced} synced, ${result.failed} failed, ${result.skipped} skipped.`,
            );
            void queryClient.invalidateQueries({
                queryKey: queryKeys.adminCompany(numericCompanyId),
            });
            void queryClient.invalidateQueries({ queryKey: [...queryKeys.all, 'organizations'] });
        } catch {
            showErrorToast('Could not sync company users to Auth0. Try again.');
        }
    };

    const handleSetCompanyUserAdmin = async (userId: number, isAdmin: boolean) => {
        setCompanyAdminPendingUserId(userId);
        try {
            await companyAdminMutations.updateCompanyUserAdmin.mutateAsync({
                userId,
                isAdmin,
            });
            showSuccessToast('Company admin access updated.');
        } catch {
            showErrorToast('Could not update company admin access. Try again.');
        } finally {
            setCompanyAdminPendingUserId(null);
        }
    };

    const handleSowRowClick = (sow: { id: string; name: string }) => {
        navigate(`/admin/sows/${sow.id}`, {
            state: {
                fromCompanyDetail: {
                    companyId: String(companyId),
                    companyLabel: companyDisplayName,
                    sowName: sow.name,
                },
            } satisfies AdminFromCompanyDetailState,
        });
    };

    const toggleExpandedTeam = (teamId: string) => {
        setExpandedTeamIds((prev) => {
            const next = new Set(prev);
            if (next.has(teamId)) {
                next.delete(teamId);
            } else {
                next.add(teamId);
            }
            return next;
        });
    };

    const startInlineTeamEdit = (teamId: number, name: string) => {
        setInlineTeamEdit({
            teamId,
            draft: name,
            originalName: name,
        });
    };

    const updateInlineTeamDraft = (teamId: number, value: string) => {
        setInlineTeamEdit((prev) =>
            prev && prev.teamId === teamId
                ? {
                      ...prev,
                      draft: value,
                      error: undefined,
                  }
                : prev,
        );
    };

    const cancelInlineTeamNameEdit = () => setInlineTeamEdit(null);

    const handleSetTeamActive = (teamId: number, isActive: boolean) => {
        setTeamActive.mutate(
            {
                teamId,
                isActive,
            },
            {
                onError: () => {
                    showErrorToast('Could not update team. Try again.');
                },
            },
        );
    };

    const handleSetMembershipActive = (teamId: number, userId: number, isActive: boolean) => {
        if (teamMembershipUpdateLockedRef.current) {
            return;
        }

        teamMembershipUpdateLockedRef.current = true;
        setTeamMembershipPending({ teamId, userId });

        void (async () => {
            try {
                await setTeamMembershipActive.mutateAsync({
                    teamId,
                    userId,
                    isActive,
                });
            } catch {
                showErrorToast('Could not update membership. Try again.');
            } finally {
                teamMembershipUpdateLockedRef.current = false;
                setTeamMembershipPending(null);
            }
        })();
    };

    const openCreatePulseSubscription = () => {
        setSelectedPulseSubscription(null);
        setIsPulseSubscriptionDialogOpen(true);
    };

    const openEditPulseSubscription = (subscription: AdminPulseSubscription) => {
        setSelectedPulseSubscription(subscription);
        setIsPulseSubscriptionDialogOpen(true);
    };

    const closePulseSubscriptionDialog = () => {
        setIsPulseSubscriptionDialogOpen(false);
    };

    const savePulseSubscription = async (input: AdminPulseSubscriptionInput) => {
        if (selectedPulseSubscription) {
            await pulseSubscriptionMutations.updateSubscription.mutateAsync({
                subscriptionId: selectedPulseSubscription.id,
                request: input,
            });
            showSuccessToast('Pulse subscription updated.');
        } else {
            await pulseSubscriptionMutations.createSubscription.mutateAsync(input);
            showSuccessToast('Pulse subscription created.');
        }
        closePulseSubscriptionDialog();
    };

    const teamActivePendingTeamId =
        setTeamActive.isPending && setTeamActive.variables?.teamId !== undefined
            ? setTeamActive.variables.teamId
            : null;
    return {
        activeTab,
        auth0OrganizationId,
        canSyncCompanyUsers,
        companyAdminsQuery,
        companyAdminMutations,
        companyAdminPendingUserId,
        companyAdminStatusByUserId,
        companyAdminStatuses,
        companyAdminUserIds,
        companyDisplayName,
        companyHasProAccess,
        companyHasPulseAccess,
        companyId,
        companyQuery,
        companyUsersQuery,
        cancelInlineTeamNameEdit,
        dismissToast,
        expandedTeamIds,
        finishInlineTeamNameEdit,
        goBackToAdmin,
        handleSetMembershipActive,
        handleSetTeamActive,
        openCreatePulseSubscription,
        openEditPulseSubscription,
        closePulseSubscriptionDialog,
        savePulseSubscription,
        handleSowRowClick,
        handleSetCompanyUserAdmin,
        handleSyncAllUnsyncedUsers,
        handleSyncCompanyToAuth0,
        handleSyncUser,
        inlineTeamEdit,
        inlineTeamNameInputRef,
        isCompanyAuth0Syncing,
        isCompanySyncedToAuth0,
        isCreateSowOpen,
        isHistoricalImportOpen,
        isCreateTeamOpen,
        isEditCompanyOpen,
        isPulseSubscriptionDialogOpen,
        numericCompanyId,
        pricingQuery,
        pulseSubscriptionTiersQuery,
        pulseSubscriptionsQuery,
        pulseSubscriptionMutations,
        selectedPulseSubscription,
        selectedUser,
        setActiveTab,
        selectActiveTab,
        setInlineTeamEdit,
        setIsCreateSowOpen,
        setIsHistoricalImportOpen,
        setIsCreateTeamOpen,
        setIsEditCompanyOpen,
        setSelectedUser,
        showErrorToast,
        showSuccessToast,
        showWarningToast,
        sowRows,
        sowsQuery,
        ssoDetailState,
        startInlineTeamEdit,
        syncUsersToAuth0,
        syncingUserId,
        teamsQuery,
        teamActivePendingTeamId,
        teamMembershipLocked: teamMembershipPending !== null,
        teamMembershipPending,
        toast,
        toggleExpandedTeam,
        updateInlineTeamDraft,
        usersPage,
        setUsersPage,
        usersSearchDraft,
        usersSearchQuery,
        setUsersSearchDraft,
        commitUsersSearch: (value = usersSearchDraft) => {
            setUsersSearchQuery(value);
            setUsersPage(1);
        },
    };
}

export { COMPANY_USERS_PAGE_SIZE };
